import React, { useState, useEffect } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { useGameStore } from '@/stores/gameStore';
import { useSessionActions } from '@/stores/gameSessionStore';
import { clearStoredSessionData, setExitInProgress } from '@/utils/sessionRecovery';
import { dbService } from '@/services/database';
import { audioManager } from '@/utils/audioManager';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';

interface FloatingSettingsProps {
  isTeacher?: boolean;
  sessionId?: string;
  playerId?: string;
}

const FloatingSettings: React.FC<FloatingSettingsProps> = ({
  isTeacher = false,
  sessionId,
  playerId
}) => {
  const navigate = useSafeNavigate();
  const isTouchDevice = useIsTouchDevice();
  const { resetGame } = useGameStore();
  const { reset: resetSessionStore } = useSessionActions();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [sfxVolume, setSfxVolume] = useState(1);

  const floatingOffsetBottom = isTouchDevice
    ? 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)'
    : '0.5rem';
  const floatingOffsetRight = isTouchDevice
    ? 'calc(env(safe-area-inset-right, 0px) + 0.5rem)'
    : '0.5rem';
  const settingsPanelBottom = isTouchDevice
    ? 'calc(env(safe-area-inset-bottom, 0px) + 3.5rem)'
    : '3.5rem';

  // Load current volume settings on mount
  useEffect(() => {
    const settings = audioManager.getVolumeSettings();
    setSfxVolume(settings.master);
  }, []);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setSfxVolume(newVolume);
    audioManager.setMasterVolume(newVolume);
  };

  const isMuted = sfxVolume === 0;

  const handleExitGame = () => {
    setShowExitWarning(true);
  };

  const handleConfirmExit = () => {
    setShowExitWarning(false);
    setIsSettingsOpen(false);

    console.log('🚪 Starting exit process...', { isTeacher, sessionId, playerId });

    // Step 1: Set exit state to prevent recovery systems
    setExitInProgress();

    // Step 2: Clear local game state
    resetGame();

    // Step 3: Clear session store to stop auto-navigation
    resetSessionStore();

    // Step 4: Clear session storage (prevents session recovery)
    clearStoredSessionData();

    // Step 5: Clear URL parameters to prevent session re-association
    const baseUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, '', baseUrl);

    // Step 6: Navigate IMMEDIATELY — don't wait for network calls
    if (isTeacher) {
      navigate('/teacher', { replace: true });
    } else {
      navigate('/', { replace: true });
    }

    // Step 7: Fire-and-forget database cleanup (don't block navigation)
    if (sessionId) {
      if (isTeacher) {
        console.log(`🏫 Teacher ending game session: ${sessionId}`);
        dbService.endGameSession(sessionId).catch((err: unknown) => {
          console.error('❌ Failed to end session in database:', err);
        });
      } else if (playerId) {
        console.log(`🎓 Student ${playerId} leaving session: ${sessionId}`);
        dbService.removePlayerFromSession(sessionId, playerId).catch((err: unknown) => {
          console.error('❌ Failed to remove player from session:', err);
        });
      }
    }

    console.log('✅ Exit process completed');
  };

  const handleCancelExit = () => {
    setShowExitWarning(false);
  };

  const handleSettingsClick = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  return (
    <>
      {/* Floating Settings Button */}
      <button
        onClick={handleSettingsClick}
        style={{
          position: 'fixed',
          bottom: floatingOffsetBottom,
          right: floatingOffsetRight,
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: 'rgba(0,0,0,0.7)',
          border: '2px solid rgba(255,255,255,0.2)',
          color: 'white',
          fontSize: '1.1rem',
          cursor: 'pointer',
          zIndex: 1001,
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.9)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.7)';
        }}
      >
        ⚙️
      </button>

      {/* Settings Panel */}
      {isSettingsOpen && (
        <div style={{
          position: 'fixed',
          bottom: settingsPanelBottom,
          right: floatingOffsetRight,
          backgroundColor: 'rgba(0,0,0,0.9)',
          border: '2px solid rgba(255,255,255,0.2)',
          borderRadius: '15px',
          padding: '1.5rem',
          backdropFilter: 'blur(15px)',
          zIndex: 1002,
          minWidth: '280px',
          color: 'white',
          boxShadow: '0 15px 35px rgba(0,0,0,0.5)'
        }}>
          <h3 style={{
            margin: '0 0 1.5rem 0',
            fontSize: '1.2rem',
            color: '#FFD700'
          }}>
            Game Settings
          </h3>

          {/* Volume Control */}
          <div style={{
            marginBottom: '1.5rem',
            padding: '0.8rem',
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderRadius: '10px'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.8rem',
              fontSize: '0.95rem',
              fontWeight: '500'
            }}>
              {isMuted ? (
                <VolumeX size={18} style={{ color: '#FF6B6B' }} />
              ) : (
                <Volume2 size={18} style={{ color: '#4CAF50' }} />
              )}
              Sound Effects
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.8rem'
            }}>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={sfxVolume}
                onChange={handleVolumeChange}
                style={{
                  flex: 1,
                  accentColor: '#4CAF50',
                  cursor: 'pointer'
                }}
              />
              <span style={{
                minWidth: '55px',
                textAlign: 'right',
                fontSize: '0.9rem',
                fontWeight: '600',
                color: isMuted ? '#FF6B6B' : '#4CAF50'
              }}>
                {isMuted ? 'Muted' : `${Math.round(sfxVolume * 100)}%`}
              </span>
            </div>
          </div>

          <button
            onClick={handleExitGame}
            style={{
              width: '100%',
              padding: '0.8rem 1rem',
              backgroundColor: '#E53E3E',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '0.5rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#C53030';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#E53E3E';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            🚪 Exit Game
          </button>

          <p style={{
            margin: '1rem 0 0 0',
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.6)',
            textAlign: 'center'
          }}>
            {isTeacher ? 'Teacher Controls' : 'Student Controls'}
          </p>
        </div>
      )}

      {/* Exit Warning Modal */}
      {showExitWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          backdropFilter: 'blur(5px)'
        }}>
          <div style={{
            backgroundColor: '#1A202C',
            border: '3px solid #E53E3E',
            borderRadius: '20px',
            padding: '3rem',
            maxWidth: '500px',
            textAlign: 'center',
            color: 'white',
            boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
            animation: 'modalBounce 0.4s ease-out'
          }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: '1rem'
            }}>
              😱
            </div>

            <h2 style={{
              color: '#E53E3E',
              fontSize: '1.8rem',
              marginBottom: '1.5rem',
              fontWeight: 'bold'
            }}>
              Wait, What?!
            </h2>

            <p style={{
              fontSize: '1.2rem',
              lineHeight: '1.6',
              marginBottom: '2rem',
              color: '#FFD700'
            }}>
              Are you sure you want to <strong>end all the fun</strong>? This can't be true!!!
              Are you ok? Do you need to talk to someone?
              <br /><br />
              <span style={{ color: '#FF6B6B', fontSize: '1.1rem' }}>
                Don't end this game, oh noooooo! 😭
              </span>
            </p>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleCancelExit}
                style={{
                  padding: '1rem 2rem',
                  backgroundColor: '#38A169',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(56,161,105,0.4)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2F855A';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(56,161,105,0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#38A169';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(56,161,105,0.4)';
                }}
              >
                🎮 Return to Game
              </button>

              <button
                onClick={handleConfirmExit}
                style={{
                  padding: '1rem 2rem',
                  backgroundColor: '#718096',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(113,128,150,0.4)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#4A5568';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(113,128,150,0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#718096';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(113,128,150,0.4)';
                }}
              >
                😔 I'm fine, just take me to the lobby
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay to close settings */}
      {isSettingsOpen && (
        <div
          onClick={() => setIsSettingsOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
        />
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes modalBounce {
          0% {
            opacity: 0;
            transform: scale(0.8) translateY(-20px);
          }
          60% {
            opacity: 1;
            transform: scale(1.05) translateY(0);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default FloatingSettings;
